import { useCallback, useEffect, useMemo, useState } from "react";
import {
    Button,
    DropdownItemProps,
    Input,
    Select,
    Pagination,
    Loader,
    Popup,
    Checkbox,
} from "semantic-ui-react";
import { toast } from "../../..";
import staticFilterOptions from "../../../components/FormsOverviewTable/staticFilterOptions.json";
import { tableDtoInit, tableFilterInit, ITableDto } from "../../../models/table";
import { getPublishedForms } from "../../../services/formsService";
import { IForm } from "../../../models/form";
import {
    bulkDeleteTables,
    getTablesData,
    getTables,
    getDevelopmentTables,
} from "../../../services/tablesService";
import { TableDisplay } from "./TableDisplay";
import { Breadcrumbs } from "../../../components/Breadcrumbs/Breadcrumbs";
import "../FiguresAndTables.css";
import { GenerateModal } from "./GenerateModal";
import { TableViewModal } from "./TableViewModal";
import { BulkDeleteModal } from "./BulkDeleteModal";
import { DeleteTableModal } from "./DeleteTableModal";

export const AdminTables = () => {
    const numberOfItemsToFetch = 15;
    const [totalRows, setTotalRows] = useState(0);
    const [page, setPage] = useState(1);
    const [tableFilter, setTableFilter] = useState(tableFilterInit);
    const [generateModalOpen, setGenerateModalOpen] = useState(false);

    const [filteredTables, setFilteredTables] = useState<ITableDto[]>([]);
    const [developmentTables, setDevelopmentTables] = useState<ITableDto[]>([]);
    const [tablesData, setTablesData] = useState<Record<string, string[][]>>({});
    const [developmentTablesData, setDevelopmentTablesData] = useState<Record<string, string[][]>>(
        {}
    );

    const [tableToEdit, setTableToEdit] = useState<ITableDto>();
    const [tableToOpen, setTableToOpen] = useState<ITableDto>(tableDtoInit);
    const [tableToDelete, setTableToDelete] = useState(tableDtoInit);

    const [formsAsDropdownProps, setFormsAsDropdownProps] = useState<DropdownItemProps[]>([]);
    const [allFormsWithAttributes, setAllFormsWithAttributes] = useState<IForm[]>([]);

    const [loadingTables, setLoadingTables] = useState(true);
    const [loadingDevelopmentTables, setLoadingDevelopmentTables] = useState(true);

    const [selectedForDelete, setSelectedForDelete] = useState<string[]>([]);
    const [deleteSelectedModalOpen, setDeleteSelectedModalOpen] = useState(false);
    const [deleteFilteredModalOpen, setDeleteFilteredModalOpen] = useState(false);

    const fetchFormsAsDropdownProps = useCallback(async () => {
        try {
            const res = await getPublishedForms();
            setAllFormsWithAttributes(res);
            setFormsAsDropdownProps(
                res.map(f => {
                    return {
                        key: f.id,
                        value: f.id,
                        text: f.name,
                    };
                })
            );
        } catch {
            toast("Error fetching forms", false, 1000);
        }
    }, []);

    useEffect(() => {
        fetchFormsAsDropdownProps();
    }, [fetchFormsAsDropdownProps]);

    const fetchTables = useCallback(async () => {
        try {
            setLoadingTables(true);
            const res = await getTables(tableFilter, page, numberOfItemsToFetch);
            setFilteredTables(res.result);
            setTotalRows(res.totalRows);
            setSelectedForDelete([]);
        } catch {
            toast("Error fetching tables", false, 1000);
        } finally {
            setLoadingTables(false);
        }
    }, [tableFilter, page]);

    useEffect(() => {
        fetchTables();
    }, [fetchTables]);

    const fetchTablesData = useCallback(async () => {
        try {
            const tableGuids = filteredTables.map(table => table.guid);
            if (tableGuids.length > 0) {
                const response = await getTablesData(tableGuids);
                setTablesData(response);
            } else {
                setTablesData({});
            }
        } catch (error) {
            toast("Error fetching table data", false, 2000);
        }
    }, [filteredTables]);

    useEffect(() => {
        fetchTablesData();
    }, [fetchTablesData]);

    const fetchDevelopmentTables = useCallback(async () => {
        try {
            setLoadingDevelopmentTables(true);
            const res = await getDevelopmentTables();
            setDevelopmentTables(res);
        } catch {
            toast("Error fetching tables under development", false, 1000);
        } finally {
            setLoadingDevelopmentTables(false);
        }
    }, []);

    useEffect(() => {
        fetchDevelopmentTables();
    }, [fetchDevelopmentTables]);

    const fetchDevelopmentTablesData = useCallback(async () => {
        try {
            const tableGuids = developmentTables.map(table => table.guid);
            if (tableGuids.length > 0) {
                const response = await getTablesData(tableGuids);
                setDevelopmentTablesData(response);
            } else {
                setDevelopmentTablesData({});
            }
        } catch (error) {
            toast("Error fetching table data", false, 2000);
        }
    }, [developmentTables]);

    useEffect(() => {
        fetchDevelopmentTablesData();
    }, [fetchDevelopmentTablesData]);

    useEffect(() => {
        setPage(1);
    }, [tableFilter]);

    const handleClearFilter = () => {
        setTableFilter(tableFilterInit);
    };

    const handleAddToSelectedForDelete = (id: string, selected: boolean) => {
        if (selected) {
            setSelectedForDelete([...selectedForDelete, id]);
        } else {
            setSelectedForDelete(selectedForDelete.filter(e => e !== id));
        }
    };

    const handleBuklDelete = useCallback(
        async (withFilter: boolean) => {
            try {
                let filter = withFilter ? tableFilter : tableFilterInit;
                let deleteArray = withFilter ? [] : selectedForDelete;

                await bulkDeleteTables(filter, deleteArray, withFilter);
                await fetchTables();

                setSelectedForDelete([]);
                if (withFilter) {
                    setDeleteFilteredModalOpen(false);
                } else {
                    setDeleteSelectedModalOpen(false);
                }
                toast("Tables successfully deleted", true, 3000);
            } catch (err) {
                toast("Error deleting tables", false, 3000);
            }
        },
        [tableFilter, selectedForDelete, fetchTables]
    );

    const isFilterEmpty = useMemo(() => {
        return tableFilter.form === "" && tableFilter.name === "" && tableFilter.year === "";
    }, [tableFilter]);

    return (
        <div className="admin-tables-container">
            <div className="container-header">
                <h1>Tables</h1>
                <Button
                    className="country-button"
                    primary
                    content="Generate"
                    onClick={() => setGenerateModalOpen(true)}
                />
            </div>
            <Breadcrumbs />
            <div className="admin-figures-tables-filter">
                <div className="filter-section">
                    <p>Name</p>
                    <Input
                        value={tableFilter.name}
                        onChange={e => setTableFilter({ ...tableFilter, name: e.target.value })}
                        icon="search"
                    />
                </div>
                <div className="filter-section year">
                    <p>Year</p>
                    <Select
                        fluid
                        clearable
                        selectOnBlur={false}
                        value={tableFilter.year}
                        options={staticFilterOptions.year}
                        onChange={(_e, data) =>
                            setTableFilter({ ...tableFilter, year: data.value as string })
                        }
                    />
                </div>
                <div className="filter-section form">
                    <p>Form</p>
                    <Select
                        fluid
                        clearable
                        selectOnBlur={false}
                        value={tableFilter.form}
                        options={formsAsDropdownProps}
                        onChange={(_e, data) =>
                            setTableFilter({ ...tableFilter, form: data.value as string })
                        }
                    />
                </div>
                <Button
                    className="filter-button"
                    primary
                    content="Clear filters"
                    onClick={handleClearFilter}
                />
                <Popup
                    content="Filter is empty"
                    disabled={!isFilterEmpty}
                    trigger={
                        <span>
                            <Button
                                basic
                                color="red"
                                content="Delete filtered"
                                disabled={isFilterEmpty}
                                onClick={() => setDeleteFilteredModalOpen(true)}
                            />
                        </span>
                    }
                />
            </div>

            <div className="admin-tables-content">
                <Loader active={loadingTables} inline="centered" />
                {!loadingTables && filteredTables.length > 0 ? (
                    <>
                        <div className="delete-selection-row">
                            <Checkbox
                                label="Select all"
                                checked={selectedForDelete.length === filteredTables.length}
                                onChange={(_e, data) => {
                                    if (data.checked) {
                                        setSelectedForDelete(filteredTables.map(f => f.guid));
                                    } else {
                                        setSelectedForDelete([]);
                                    }
                                }}
                            />
                            <Popup
                                content="No tables selected..."
                                disabled={selectedForDelete.length > 0}
                                trigger={
                                    <span>
                                        <Button
                                            primary
                                            content="Delete selected"
                                            disabled={selectedForDelete.length === 0}
                                            onClick={() => setDeleteSelectedModalOpen(true)}
                                        />
                                    </span>
                                }
                            />
                        </div>
                        {filteredTables.map(table => (
                            <TableDisplay
                                key={table.guid}
                                table={table}
                                selectedForDelete={selectedForDelete.includes(table.guid)}
                                setSelectedForDelete={handleAddToSelectedForDelete}
                                setTableToEdit={setTableToEdit}
                                setTableToOpen={setTableToOpen}
                                setTableToDelete={setTableToDelete}
                                tableData={tablesData[table.guid] ?? []}
                                refreshTables={() => {
                                    fetchTables();
                                    fetchDevelopmentTables();
                                }}
                                handleClearFilter={handleClearFilter}
                            />
                        ))}
                    </>
                ) : (
                    <p>There are no tables.</p>
                )}
            </div>

            <div className="figures-tables-pagination-container">
                <Pagination
                    activePage={page}
                    totalPages={Math.ceil(totalRows / numberOfItemsToFetch)}
                    onPageChange={(_event, data) => setPage(data.activePage as number)}
                />
            </div>

            <div className="container-header">
                <h1>Tables under development</h1>
            </div>
            <div className="admin-tables-content">
                <Loader active={loadingDevelopmentTables} inline="centered" />
                {!loadingDevelopmentTables && developmentTables.length > 0 ? (
                    <>
                        {developmentTables.map(table => {
                            return (
                                <TableDisplay
                                    key={table.guid}
                                    table={table}
                                    selectedForDelete={selectedForDelete.includes(table.guid)}
                                    setSelectedForDelete={() => {}}
                                    setTableToEdit={setTableToEdit}
                                    setTableToOpen={setTableToOpen}
                                    setTableToDelete={setTableToDelete}
                                    tableData={developmentTablesData[table.guid] ?? []}
                                    refreshTables={() => {
                                        fetchTables();
                                        fetchDevelopmentTables();
                                    }}
                                    handleClearFilter={handleClearFilter}
                                />
                            );
                        })}
                    </>
                ) : (
                    <p>There are no tables.</p>
                )}
            </div>

            {(generateModalOpen || Boolean(tableToEdit)) && (
                <GenerateModal
                    generateModalOpen={generateModalOpen || Boolean(tableToEdit)}
                    tableToEdit={tableToEdit}
                    setTableToEdit={setTableToEdit}
                    closeGenerateModal={() => {
                        setGenerateModalOpen(false);
                        setTableToEdit(undefined);
                    }}
                    allFormsWithAttributes={allFormsWithAttributes}
                    refreshTables={() => {
                        fetchTables();
                        fetchDevelopmentTables();
                    }}
                />
            )}

            {tableToOpen.guid && (
                <TableViewModal
                    tableToOpen={tableToOpen}
                    setTableToOpen={setTableToOpen}
                    data={
                        tablesData[tableToOpen.guid] ??
                        developmentTablesData[tableToOpen.guid] ??
                        []
                    }
                />
            )}

            <DeleteTableModal
                deleteTableModalOpen={tableToDelete.guid !== ""}
                closeDeleteTableModal={() => setTableToDelete(tableDtoInit)}
                tableToDelete={tableToDelete}
                handleClearFilter={handleClearFilter}
                refreshTables={() => {
                    fetchTables();
                    fetchDevelopmentTables();
                }}
            />

            <BulkDeleteModal
                modalOpen={deleteSelectedModalOpen}
                setModalOpen={setDeleteSelectedModalOpen}
                handleBuklDelete={handleBuklDelete}
            />

            <BulkDeleteModal
                modalOpen={deleteFilteredModalOpen}
                setModalOpen={setDeleteFilteredModalOpen}
                handleBuklDelete={handleBuklDelete}
                isFiltered={true}
            />
        </div>
    );
};
